/**
 * Single source of truth for role → route access and sidebar menus.
 * Consumed by App.tsx, DashboardLayout, and FacilityRouteGuard.
 */

export type UserRole =
  | 'admin'
  | 'supervisor'
  | 'doctor'
  | 'caregiver'
  | 'facility_owner';

export const ALL_ROLES: readonly UserRole[] = [
  'admin',
  'supervisor',
  'doctor',
  'caregiver',
  'facility_owner',
] as const;

export type MenuIconKey =
  | 'dashboard'
  | 'people'
  | 'business'
  | 'security'
  | 'assignment'
  | 'schedule'
  | 'person'
  | 'description'
  | 'task'
  | 'assessment'
  | 'medication'
  | 'calendar'
  | 'repeat'
  | 'shopping';

export type RouteScope = 'global' | 'facility';

export interface RouteAccessDef {
  /** Roles allowed to access this route */
  roles: readonly UserRole[];
  /**
   * `facility` routes go through FacilityRouteGuard (facility context required
   * for non-admin users). `global` routes use a simple role check.
   */
  scope: RouteScope;
}

export interface MenuItemConfig {
  text: string;
  path: string;
  icon: MenuIconKey;
  roles: readonly UserRole[];
  comingSoon?: boolean;
  comingSoonGroup?: string;
  /** When true, only include if the user has any facility context */
  requiresFacilityContext?: boolean;
}

/** Canonical route paths (templates for param routes). */
export const ROUTE_ACCESS = {
  '/dashboard': { roles: ALL_ROLES, scope: 'global' },
  '/profile': { roles: ALL_ROLES, scope: 'global' },
  '/notifications': { roles: ALL_ROLES, scope: 'global' },
  '/notifications/preferences': { roles: ALL_ROLES, scope: 'global' },

  '/users': { roles: ['admin', 'supervisor'] as const, scope: 'global' },
  '/facilities': { roles: ['admin', 'supervisor'] as const, scope: 'global' },
  '/team-management': { roles: ['admin', 'supervisor'] as const, scope: 'global' },
  '/role-management': { roles: ['admin'] as const, scope: 'global' },
  '/access-management': { roles: ['supervisor', 'facility_owner'] as const, scope: 'global' },

  '/medications': {
    roles: ['admin', 'supervisor', 'doctor', 'caregiver', 'facility_owner'] as const,
    scope: 'global',
  },
  '/care-plans': {
    roles: ['admin', 'supervisor', 'doctor', 'caregiver'] as const,
    scope: 'global',
  },
  '/schedules': {
    roles: ['admin', 'supervisor', 'doctor', 'caregiver'] as const,
    scope: 'global',
  },

  '/facility/residents': {
    roles: ['admin', 'supervisor', 'doctor', 'caregiver', 'facility_owner'] as const,
    scope: 'facility',
  },
  '/residents/:id': {
    roles: ['admin', 'supervisor', 'doctor', 'caregiver', 'facility_owner'] as const,
    scope: 'facility',
  },
  '/facility/contacts': {
    roles: ['admin', 'supervisor', 'caregiver'] as const,
    scope: 'facility',
  },
  '/facility/documents': {
    roles: ['admin', 'supervisor', 'facility_owner', 'doctor', 'caregiver'] as const,
    scope: 'facility',
  },
  '/facility/tasks': {
    roles: ['admin', 'supervisor', 'doctor', 'caregiver', 'facility_owner'] as const,
    scope: 'facility',
  },
  '/facility/supplies': {
    roles: ALL_ROLES,
    scope: 'facility',
  },
  '/facility/prn-audit': {
    roles: ['admin', 'supervisor', 'doctor', 'caregiver', 'facility_owner'] as const,
    scope: 'facility',
  },
  '/facility/task-templates': {
    roles: ['admin', 'supervisor', 'doctor', 'caregiver', 'facility_owner'] as const,
    scope: 'facility',
  },
  '/facility/inspections': {
    roles: ['admin', 'supervisor'] as const,
    scope: 'facility',
  },
  '/facility/users': {
    roles: ['admin', 'supervisor', 'facility_owner'] as const,
    scope: 'facility',
  },
  '/facility/team-performance': {
    roles: ['admin', 'supervisor', 'facility_owner'] as const,
    scope: 'facility',
  },
} as const satisfies Record<string, RouteAccessDef>;

export type AppRoutePath = keyof typeof ROUTE_ACCESS;

function isUserRole(role: string | undefined | null): role is UserRole {
  return !!role && (ALL_ROLES as readonly string[]).includes(role);
}

/**
 * Resolve a concrete location pathname (e.g. `/residents/abc`) or a route
 * template (`/residents/:id`) to its access definition.
 */
export function resolveRouteAccess(
  path: string
): RouteAccessDef | undefined {
  if (path in ROUTE_ACCESS) {
    return ROUTE_ACCESS[path as AppRoutePath];
  }

  // Parametric resident detail: /residents/:id
  if (/^\/residents\/[^/]+$/.test(path)) {
    return ROUTE_ACCESS['/residents/:id'];
  }

  // Nested notification preferences already keyed; other /notifications/*
  if (path.startsWith('/notifications')) {
    return ROUTE_ACCESS['/notifications'];
  }

  return undefined;
}

export function getAllowedRolesForRoute(path: string): readonly UserRole[] {
  return resolveRouteAccess(path)?.roles ?? [];
}

export function canAccessRoute(
  role: string | undefined | null,
  path: string
): boolean {
  if (!isUserRole(role)) return false;
  const access = resolveRouteAccess(path);
  if (!access) {
    // Unknown routes: allow any authenticated role (catch-all redirects elsewhere)
    return true;
  }
  return (access.roles as readonly string[]).includes(role);
}

export function isFacilityScopedRoute(path: string): boolean {
  return resolveRouteAccess(path)?.scope === 'facility';
}

/** Menu shown when the user has an assigned facilityId (facility-first nav). */
export const FACILITY_SCOPED_MENU: readonly MenuItemConfig[] = [
  {
    text: 'Dashboard',
    icon: 'dashboard',
    path: '/dashboard',
    roles: ALL_ROLES,
  },
  {
    text: 'Residents',
    icon: 'person',
    path: '/facility/residents',
    roles: ['admin', 'caregiver', 'doctor', 'supervisor', 'facility_owner'],
  },
  {
    text: 'Tasks',
    icon: 'task',
    path: '/facility/tasks',
    roles: ['admin', 'caregiver', 'doctor', 'supervisor', 'facility_owner'],
  },
  {
    text: 'Groceries & Supplies',
    icon: 'shopping',
    path: '/facility/supplies',
    roles: ALL_ROLES,
  },
  {
    text: 'Task Templates',
    icon: 'repeat',
    path: '/facility/task-templates',
    roles: ['admin', 'supervisor'],
  },
  {
    text: 'Medications',
    icon: 'medication',
    path: '/medications',
    roles: ['admin', 'caregiver', 'doctor', 'supervisor', 'facility_owner'],
  },
  {
    text: 'PRN Audit',
    icon: 'medication',
    path: '/facility/prn-audit',
    roles: ['admin', 'caregiver', 'doctor', 'supervisor', 'facility_owner'],
  },
  {
    text: 'Schedules',
    icon: 'calendar',
    path: '/schedules',
    roles: ['admin', 'caregiver', 'doctor', 'supervisor'],
    comingSoon: true,
    comingSoonGroup: 'schedules_inspections',
  },
  {
    text: 'Inspections',
    icon: 'assessment',
    path: '/facility/inspections',
    roles: ['admin', 'caregiver', 'doctor', 'supervisor'],
    comingSoon: true,
    comingSoonGroup: 'schedules_inspections',
  },
  {
    text: 'Team',
    icon: 'people',
    path: '/facility/users',
    roles: ['admin', 'doctor', 'supervisor', 'facility_owner'],
  },
  {
    text: 'Team Performance',
    icon: 'assessment',
    path: '/facility/team-performance',
    roles: ['admin', 'supervisor', 'facility_owner'],
  },
  {
    text: 'Documents',
    icon: 'description',
    path: '/facility/documents',
    roles: ['admin', 'caregiver', 'doctor', 'supervisor', 'facility_owner'],
  },
];

const DASHBOARD_ITEM: MenuItemConfig = {
  text: 'Dashboard',
  icon: 'dashboard',
  path: '/dashboard',
  roles: ALL_ROLES,
};

/**
 * Global (non-facility-assigned) menus by role.
 * Items with `requiresFacilityContext` are omitted when the user has no facility context.
 */
export const GLOBAL_MENU_BY_ROLE: Record<UserRole, readonly MenuItemConfig[]> = {
  admin: [
    DASHBOARD_ITEM,
    { text: 'Users', icon: 'people', path: '/users', roles: ['admin'] },
    { text: 'Facilities', icon: 'business', path: '/facilities', roles: ['admin'] },
    {
      text: 'Team Management',
      icon: 'people',
      path: '/team-management',
      roles: ['admin'],
    },
    {
      text: 'Role Management',
      icon: 'assignment',
      path: '/role-management',
      roles: ['admin'],
    },
    {
      text: 'Medications',
      icon: 'medication',
      path: '/medications',
      roles: ['admin'],
    },
    {
      text: 'PRN Audit',
      icon: 'medication',
      path: '/facility/prn-audit',
      roles: ['admin'],
      requiresFacilityContext: true,
    },
    {
      text: 'Groceries & Supplies',
      icon: 'shopping',
      path: '/facility/supplies',
      roles: ['admin'],
      requiresFacilityContext: true,
    },
    {
      text: 'Schedules',
      icon: 'calendar',
      path: '/schedules',
      roles: ['admin'],
      comingSoon: true,
    },
  ],
  supervisor: [
    DASHBOARD_ITEM,
    { text: 'Users', icon: 'people', path: '/users', roles: ['supervisor'] },
    {
      text: 'Facilities',
      icon: 'business',
      path: '/facilities',
      roles: ['supervisor'],
    },
    {
      text: 'Team Management',
      icon: 'people',
      path: '/team-management',
      roles: ['supervisor'],
    },
    {
      text: 'Access Management',
      icon: 'security',
      path: '/access-management',
      roles: ['supervisor'],
    },
    {
      text: 'Medications',
      icon: 'medication',
      path: '/medications',
      roles: ['supervisor'],
    },
    {
      text: 'PRN Audit',
      icon: 'medication',
      path: '/facility/prn-audit',
      roles: ['supervisor'],
      requiresFacilityContext: true,
    },
    {
      text: 'Documents',
      icon: 'description',
      path: '/facility/documents',
      roles: ['supervisor'],
      requiresFacilityContext: true,
    },
    {
      text: 'Groceries & Supplies',
      icon: 'shopping',
      path: '/facility/supplies',
      roles: ['supervisor'],
      requiresFacilityContext: true,
    },
    {
      text: 'Team Performance',
      icon: 'assessment',
      path: '/facility/team-performance',
      roles: ['supervisor'],
      requiresFacilityContext: true,
    },
    {
      text: 'Schedules',
      icon: 'calendar',
      path: '/schedules',
      roles: ['supervisor'],
      comingSoon: true,
    },
  ],
  facility_owner: [
    DASHBOARD_ITEM,
    {
      text: 'Residents',
      icon: 'person',
      path: '/facility/residents',
      roles: ['facility_owner'],
      requiresFacilityContext: true,
    },
    {
      text: 'Team',
      icon: 'people',
      path: '/facility/users',
      roles: ['facility_owner'],
      requiresFacilityContext: true,
    },
    {
      text: 'Team Performance',
      icon: 'assessment',
      path: '/facility/team-performance',
      roles: ['facility_owner'],
      requiresFacilityContext: true,
    },
    {
      text: 'Tasks',
      icon: 'task',
      path: '/facility/tasks',
      roles: ['facility_owner'],
      requiresFacilityContext: true,
    },
    {
      text: 'Groceries & Supplies',
      icon: 'shopping',
      path: '/facility/supplies',
      roles: ['facility_owner'],
      requiresFacilityContext: true,
    },
    {
      text: 'Medications',
      icon: 'medication',
      path: '/medications',
      roles: ['facility_owner'],
      requiresFacilityContext: true,
    },
    {
      text: 'PRN Audit',
      icon: 'medication',
      path: '/facility/prn-audit',
      roles: ['facility_owner'],
      requiresFacilityContext: true,
    },
    {
      text: 'Documents',
      icon: 'description',
      path: '/facility/documents',
      roles: ['facility_owner'],
      requiresFacilityContext: true,
    },
    {
      text: 'Access Management',
      icon: 'security',
      path: '/access-management',
      roles: ['facility_owner'],
    },
  ],
  doctor: [
    DASHBOARD_ITEM,
    { text: 'Patients', icon: 'people', path: '/patients', roles: ['doctor'] },
    {
      text: 'Appointments',
      icon: 'schedule',
      path: '/appointments',
      roles: ['doctor'],
    },
    {
      text: 'Medical Records',
      icon: 'assignment',
      path: '/medical-records',
      roles: ['doctor'],
    },
    {
      text: 'Medications',
      icon: 'medication',
      path: '/medications',
      roles: ['doctor'],
    },
    {
      text: 'PRN Audit',
      icon: 'medication',
      path: '/facility/prn-audit',
      roles: ['doctor'],
      requiresFacilityContext: true,
    },
    {
      text: 'Documents',
      icon: 'description',
      path: '/facility/documents',
      roles: ['doctor'],
      requiresFacilityContext: true,
    },
    {
      text: 'Groceries & Supplies',
      icon: 'shopping',
      path: '/facility/supplies',
      roles: ['doctor'],
      requiresFacilityContext: true,
    },
    {
      text: 'Schedules',
      icon: 'calendar',
      path: '/schedules',
      roles: ['doctor'],
      comingSoon: true,
    },
  ],
  caregiver: [
    DASHBOARD_ITEM,
    {
      text: 'Residents',
      icon: 'person',
      path: '/facility/residents',
      roles: ['caregiver'],
      requiresFacilityContext: true,
    },
    {
      text: 'Tasks',
      icon: 'task',
      path: '/facility/tasks',
      roles: ['caregiver'],
      requiresFacilityContext: true,
    },
    {
      text: 'Groceries & Supplies',
      icon: 'shopping',
      path: '/facility/supplies',
      roles: ['caregiver'],
      requiresFacilityContext: true,
    },
    {
      text: 'Medications',
      icon: 'medication',
      path: '/medications',
      roles: ['caregiver'],
      requiresFacilityContext: true,
    },
    {
      text: 'PRN Audit',
      icon: 'medication',
      path: '/facility/prn-audit',
      roles: ['caregiver'],
      requiresFacilityContext: true,
    },
    {
      text: 'Schedules',
      icon: 'calendar',
      path: '/schedules',
      roles: ['caregiver'],
      comingSoon: true,
      requiresFacilityContext: true,
    },
    {
      text: 'Documents',
      icon: 'description',
      path: '/facility/documents',
      roles: ['caregiver'],
      requiresFacilityContext: true,
    },
  ],
};

export interface MenuItemsOptions {
  role: UserRole | string | undefined | null;
  /** True when getUserFacilityId(user) is set — uses facility-scoped menu */
  hasAssignedFacilityId: boolean;
  /** True when assigned facility, selectedFacilityId, or availableFacilities exist */
  hasFacilityContext: boolean;
}

/**
 * Sidebar menu items for the current user, matching prior DashboardLayout logic.
 * Caller still filters by `item.roles.includes(role)` if desired (items are
 * already role-scoped).
 */
export function getMenuItemsForUser(options: MenuItemsOptions): MenuItemConfig[] {
  const { role, hasAssignedFacilityId, hasFacilityContext } = options;
  if (!isUserRole(role)) {
    return [DASHBOARD_ITEM];
  }

  if (hasAssignedFacilityId) {
    return FACILITY_SCOPED_MENU.filter((item) => item.roles.includes(role));
  }

  const roleMenu = GLOBAL_MENU_BY_ROLE[role] ?? [DASHBOARD_ITEM];

  // Caregiver with no facility context: dashboard only (preserves prior behavior)
  if (role === 'caregiver' && !hasFacilityContext) {
    return [DASHBOARD_ITEM];
  }

  return roleMenu.filter((item) => {
    if (!item.roles.includes(role)) return false;
    if (item.requiresFacilityContext && !hasFacilityContext) return false;
    return true;
  });
}
